import store, { setProductModel } from '../state';
import ManifestTimelineHelper, { PRODUCT_META } from '../services/ManifestTimelineHelper';
import ForecastAnimationService from '../services/ForecastAnimationService';
import LayoutManager from '../services/LayoutManager';
import { LayerFirstTimeToast } from './panels/LayerFirstTimeToast';
import * as SunCalc from 'suncalc';
import { getMoonPhaseDetails } from '../utils/astronomy';

export class Timeline {
  private slider: HTMLInputElement;
  private timeLabel: HTMLElement;
  private opacitySlider: HTMLInputElement;
  private opacityLabel: HTMLElement;
  private eventBadge: HTMLElement | null = null;
  private availabilityContainer: HTMLElement | null = null;
  private forecastToolbar: HTMLElement | null = null;
  private forecastPlayBtn: HTMLElement | null = null;
  private durationButtons: NodeListOf<Element>;
  private speedButtons: NodeListOf<Element>;
  private loopBracket: HTMLElement | null = null;
  private sunMoonCheck: HTMLInputElement | null = null;
  private forecastCoverageCheck: HTMLInputElement | null = null;
  private dataStatusBtn: HTMLElement | null = null;
  private horizonToggleGroup: HTMLElement | null = null;
  private horizonButtons: NodeListOf<Element> | null = null;
  private saveLayoutBtn: HTMLElement | null = null;
  private readonly defaultTimeZone: string =
    typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'America/Los_Angeles';
  private dtfCache: Map<string, Intl.DateTimeFormat> = new Map();
  private lastScaleBarSig: string = '';
  private lastSolarLunarSig: string = '';
  private lastAvailabilitySig: string = '';
  private lastTimeLabelText: string = '';
  private lastManifestRef: any = null;
  private lastAvailabilityMapRef: any = null;
  private lastKpForecastRef: any = null;
  private dataRevCounter: number = 0;

  private getFormatter(cacheKey: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
    let fmt = this.dtfCache.get(cacheKey);
    if (!fmt) {
      fmt = new Intl.DateTimeFormat('en-US', options);
      this.dtfCache.set(cacheKey, fmt);
    }
    return fmt;
  }

  constructor() {
    this.slider = document.getElementById('timeline-slider') as HTMLInputElement;
    this.timeLabel = document.getElementById('current-time-label')!;
    this.opacitySlider = document.getElementById('opacity-slider') as HTMLInputElement;
    this.opacityLabel = document.getElementById('opacity-value')!;
    this.eventBadge = document.getElementById('timeline-event-badge');
    this.availabilityContainer = document.getElementById('timeline-availability-bars');
    this.forecastToolbar = document.getElementById('forecast-animation-toolbar');
    this.forecastPlayBtn = document.getElementById('forecast-play-btn');
    this.durationButtons = document.querySelectorAll('.forecast-duration-btn');
    this.speedButtons = document.querySelectorAll('.forecast-speed-btn');
    this.loopBracket = document.getElementById('timeline-loop-bracket');
    this.sunMoonCheck = document.getElementById('gw-timeline-sun-moon-check') as HTMLInputElement | null;
    this.forecastCoverageCheck = document.getElementById('gw-timeline-forecast-coverage-check') as HTMLInputElement | null;
    this.dataStatusBtn = document.getElementById('timeline-data-status-btn');
    this.horizonToggleGroup = document.getElementById('timeline-horizon-group');
    this.horizonButtons = document.querySelectorAll('.timeline-horizon-btn');
    this.saveLayoutBtn = document.getElementById('timeline-save-layout-btn');
    this.init();
  }

  private formatForecastEventDescription(event: string, dateStr: string, kpVal?: number | null, isAvailable?: boolean): string {
    if (!event || !dateStr) return '';
    const cleanEvent = event.toLowerCase().trim();
    const parts = dateStr.split('-').map(Number);
    if (parts.length < 3) return '';
    const [yr, mo, dy] = parts;
    if (isNaN(yr) || isNaN(mo) || isNaN(dy)) return '';
    
    const eventDate = new Date(Date.UTC(yr, mo - 1, dy));

    if (cleanEvent === 'aurora' || cleanEvent.startsWith('aurora')) {
      const nextDate = new Date(Date.UTC(yr, mo - 1, dy + 1));
      const d1Fmt = eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      const d2Fmt = nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      
      const availableDates = store.getState().auroraAvailableDates;
      const resolvedAvailable = isAvailable !== undefined
        ? isAvailable
        : (availableDates && availableDates.length > 0 ? availableDates.includes(dateStr) : (kpVal !== undefined && kpVal !== null));

      if (!resolvedAvailable) {
        return `No Aurora Forecast Available for night of ${d1Fmt} - ${d2Fmt}`;
      }

      const base = `Aurora forecast night of ${d1Fmt} - ${d2Fmt}`;
      if (kpVal !== undefined && kpVal !== null) {
        return `${base} • Kp ${kpVal.toFixed(1)}`;
      }
      return base;
    }

    const month = eventDate.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    const day = eventDate.getUTCDate();
    const formattedDateStr = `${month} ${day}`;

    if (cleanEvent === 'sunrise' || cleanEvent === 'morning') {
      return `Sunrise ${formattedDateStr}`;
    } else if (cleanEvent === 'sunset' || cleanEvent === 'evening') {
      return `Sunset ${formattedDateStr}`;
    } else if (cleanEvent === 'goldenhour_morning') {
      return `Golden Hour Morning ${formattedDateStr}`;
    } else if (cleanEvent === 'goldenhour_evening') {
      return `Golden Hour Evening ${formattedDateStr}`;
    } else if (cleanEvent === 'alpenglow_morning') {
      return `Alpenglow Morning ${formattedDateStr}`;
    } else if (cleanEvent === 'alpenglow_evening') {
      return `Alpenglow Evening ${formattedDateStr}`;
    }
    return '';
  }

  private getKpForTime(kpForecast: any[], time: Date): { kp: number; observed?: string; noaa_scale?: string | null } | null {
    if (!kpForecast || kpForecast.length === 0 || !time) return null;
    const targetMs = time.getTime();
    let best: any = null;
    let minDiff = Infinity;
    for (const item of kpForecast) {
      if (!item.time_tag) continue;
      const timeStr = item.time_tag.endsWith('Z') ? item.time_tag : item.time_tag + 'Z';
      const itemMs = Date.parse(timeStr);
      if (isNaN(itemMs)) continue;
      const diff = Math.abs(itemMs - targetMs);
      if (diff < minDiff && diff <= 3 * 3600 * 1000) {
        minDiff = diff;
        best = item;
      }
    }
    return best;
  }

  private getAuroraTargetDateForTime(d: Date, tz?: string): string {
    const timeZone = tz || this.defaultTimeZone;

    const dFmt = this.getFormatter(`aurora|${timeZone}`, {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      hour12: false
    });
    const parts = dFmt.formatToParts(d);
    const yr = parseInt(parts.find(p => p.type === 'year')?.value || '2026', 10);
    const mo = parseInt(parts.find(p => p.type === 'month')?.value || '8', 10);
    const dy = parseInt(parts.find(p => p.type === 'day')?.value || '5', 10);
    const hr = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);

    const localDate = new Date(Date.UTC(yr, mo - 1, dy));
    if (hr < 12) {
      localDate.setUTCDate(localDate.getUTCDate() - 1);
    }
    return localDate.toISOString().split('T')[0];
  }

  private getStepDate(step: { date: string; hour: string; raw?: string }): Date {
    return ManifestTimelineHelper.getStepDate(step);
  }

  private updateTimelineHeight() {
    const el = document.getElementById('timeline-container');
    if (el) {
      const height = el.offsetHeight;
      document.documentElement.style.setProperty('--timeline-height', `${height + 2}px`);
    }
  }

  private init() {
    this.updateTimelineHeight();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.updateTimelineHeight());
      const el = document.getElementById('timeline-container');
      if (el) observer.observe(el);
    }

    const onSliderUpdate = () => {
      const curState = store.getState();
      // Auto-pause forecast or observed animation if running/buffering when user manually drags slider
      if (curState.forecastAnimationPlaying || curState.forecastAnimationBuffering) {
        ForecastAnimationService.getInstance().stop();
      }
      if (
        curState.liveWeatherSatellitePlaying ||
        curState.liveWeatherRadarPlaying ||
        curState.liveWeatherObservedBuffering
      ) {
        const liveWeather = (window as any).liveWeatherLayer;
        if (liveWeather && typeof liveWeather.pauseObservedAnimation === 'function') {
          liveWeather.pauseObservedAnimation();
        } else {
          store.update({
            liveWeatherSatellitePlaying: false,
            liveWeatherRadarPlaying: false,
            liveWeatherObservedBuffering: false
          });
        }
      }

      const idx = Math.round(parseFloat(this.slider.value));
      this.slider.step = '1';
      const state = store.getState();
      if (state.timelineRange && state.timelineRange[idx]) {
        const step = state.timelineRange[idx];
        const dateObj = this.getStepDate(step);
        const updates: any = {
          currentTimeStep: dateObj,
          forecastAnimationStartTime: null
        };

        // When both Observed and Forecast layers are active, switch dock playback mode automatically
        // based on scrubbing into the past/NOW (Observed 2/4/8h) vs. future (Forecast 12/24/48h)
        const hasObserved = Boolean(
          state.liveWeatherRadarEnabled ||
          state.liveWeatherRadarLightningEnabled ||
          state.liveWeatherSatelliteEnabled
        );
        const hasForecast = ForecastAnimationService.getInstance().hasActiveForecastLayers(state);
        if (hasObserved && hasForecast) {
          const nowDate = ManifestTimelineHelper.getNowDate(state.timelineRange);
          updates.activeDockPlaybackMode =
            dateObj.getTime() <= nowDate.getTime() + 60 * 1000 ? 'observed' : 'forecast';
        }

        store.update(updates);
      }
    };

    this.slider.addEventListener('input', onSliderUpdate);
    this.slider.addEventListener('change', onSliderUpdate);

    ForecastAnimationService.getInstance().onFrame((playheadTimeMs) => {
      this.updateContinuousPlayhead(playheadTimeMs);
    });

    if (this.forecastPlayBtn) {
      this.forecastPlayBtn.addEventListener('click', () => {
        const state = store.getState();
        const animService = ForecastAnimationService.getInstance();
        const liveWeather = (window as any).liveWeatherLayer;
        const isObservedActivePlay = Boolean(
          state.liveWeatherObservedBuffering ||
          state.liveWeatherRadarPlaying ||
          state.liveWeatherSatellitePlaying
        );
        const isForecastActivePlay = Boolean(
          state.forecastAnimationBuffering || state.forecastAnimationPlaying
        );

        if (isObservedActivePlay) {
          if (liveWeather && typeof liveWeather.stopObservedAnimation === 'function') {
            liveWeather.stopObservedAnimation(true);
          } else {
            store.update({
              liveWeatherRadarPlaying: false,
              liveWeatherSatellitePlaying: false,
              liveWeatherObservedBuffering: false
            });
          }
          return;
        }
        if (isForecastActivePlay) {
          animService.stop();
          return;
        }

        const mode = this.resolvePlaybackMode(state);
        if (mode === 'observed') {
          if (liveWeather && typeof liveWeather.startObservedAnimation === 'function') {
            void liveWeather.startObservedAnimation(false);
          } else {
            const hasRadar = Boolean(state.liveWeatherRadarEnabled || state.liveWeatherRadarLightningEnabled);
            const hasSat = Boolean(state.liveWeatherSatelliteEnabled);
            store.update({
              liveWeatherRadarPlaying: hasRadar,
              liveWeatherSatellitePlaying: hasSat,
              activeDockPlaybackMode: 'observed'
            });
          }
        } else if (mode === 'forecast') {
          void animService.start();
        }
      });
    }

    this.durationButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const hours = parseInt((btn as HTMLElement).dataset.hours || '12', 10);
        const state = store.getState();
        const animService = ForecastAnimationService.getInstance();
        const mode = this.resolvePlaybackMode(state);

        if (mode === 'observed') {
          store.update({
            liveWeatherRadarLoopHours: hours,
            liveWeatherSatelliteLoopHours: hours
          });
        } else if (mode === 'forecast') {
          store.update({ forecastAnimationDurationHours: hours });
          if (store.getState().forecastAnimationPlaying) {
            animService.updateLoopSettings();
          }
        }
      });
    });

    this.speedButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const speed = parseFloat((btn as HTMLElement).dataset.speed || '2.0');
        const state = store.getState();
        const animService = ForecastAnimationService.getInstance();
        const mode = this.resolvePlaybackMode(state);

        if (mode === 'observed') {
          store.update({
            liveWeatherRadarPlaybackSpeed: speed,
            liveWeatherSatPlaybackSpeed: speed
          });
        } else if (mode === 'forecast') {
          store.update({ forecastAnimationSpeed: speed });
          if (store.getState().forecastAnimationPlaying) {
            animService.updateLoopSettings();
          }
        }
      });
    });

    this.opacitySlider.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const opacity = parseFloat(target.value) / 100;
      store.update({ weatherOpacity: opacity });
    });

    this.sunMoonCheck?.addEventListener('change', () => {
      store.update({ timelineSunMoonPhaseEnabled: this.sunMoonCheck!.checked });
    });

    this.forecastCoverageCheck?.addEventListener('change', () => {
      store.update({ timelineForecastCoverageEnabled: this.forecastCoverageCheck!.checked });
    });

    this.dataStatusBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      const modal = (window as any).__dataStatusModal;
      if (modal && typeof modal.open === 'function') {
        modal.open();
      }
    });

    this.horizonButtons?.forEach(btn => {
      btn.addEventListener('click', () => {
        const horizon = parseInt((btn as HTMLElement).dataset.horizon || '5', 10) as 5 | 10;
        const maxHours = horizon === 10 ? 240 : 120;
        const newRange = ManifestTimelineHelper.generateMasterTimelineRange(undefined, maxHours);
        
        const updates: any = {
          timelineHorizon: horizon,
          timelineRange: newRange,
          isUiLayoutDirty: true
        };

        const current = store.getState().currentTimeStep;
        if (current && newRange.length > 0) {
          const lastStep = newRange[newRange.length - 1];
          const lastStepDate = this.getStepDate(lastStep);
          if (current.getTime() > lastStepDate.getTime()) {
            updates.currentTimeStep = lastStepDate;
          }
        }

        store.update(updates);
      });
    });

    this.saveLayoutBtn?.addEventListener('click', async (e) => {
      e.stopPropagation();
      await this.saveCurrentLayout();
    });

    // Subscribe to state changes
    let lastOpacity = store.getState().weatherOpacity;
    if (this.opacitySlider && lastOpacity !== undefined) {
      this.opacitySlider.value = String(Math.round(lastOpacity * 100));
      if (this.opacityLabel) {
        this.opacityLabel.innerText = `${Math.round(lastOpacity * 100)}%`;
      }
    }
    
    store.subscribe((state) => {
      // 1. Sync opacity slider
      if (state.weatherOpacity !== lastOpacity) {
        lastOpacity = state.weatherOpacity;
        if (this.opacitySlider) {
          this.opacitySlider.value = String(Math.round(state.weatherOpacity * 100));
        }
        if (this.opacityLabel) {
          this.opacityLabel.innerText = `${Math.round(state.weatherOpacity * 100)}%`;
        }
      }

      if (this.sunMoonCheck && this.sunMoonCheck.checked !== (state.timelineSunMoonPhaseEnabled !== false)) {
        this.sunMoonCheck.checked = state.timelineSunMoonPhaseEnabled !== false;
      }
      if (this.forecastCoverageCheck && this.forecastCoverageCheck.checked !== (state.timelineForecastCoverageEnabled !== false)) {
        this.forecastCoverageCheck.checked = state.timelineForecastCoverageEnabled !== false;
      }

      // Sync Horizon Selector Pill
      const hasExt = ManifestTimelineHelper.hasExtendedForecast(state);
      if (this.horizonToggleGroup) {
        this.horizonToggleGroup.style.display = hasExt ? 'flex' : 'none';
        if (this.horizonButtons) {
          this.horizonButtons.forEach(btn => {
            const h = parseInt((btn as HTMLElement).dataset.horizon || '5', 10);
            if (h === (state.timelineHorizon || 10)) {
              btn.classList.add('active');
            } else {
              btn.classList.remove('active');
            }
          });
        }
      }

      // Sync Save Layout Button
      if (this.saveLayoutBtn) {
        if (state.isUiLayoutDirty && state.isAuthenticated) {
          this.saveLayoutBtn.style.display = 'inline-flex';
        } else if (!this.saveLayoutBtn.classList.contains('saved')) {
          this.saveLayoutBtn.style.display = 'none';
        }
      }

      // 2. Sync timeline elements
      if (state.timelineRange.length > 0) {
        const nextMax = String(state.timelineRange.length - 1);
        if (this.slider.max !== nextMax) {
          this.slider.max = nextMax;
        }
        
        let badgeText = '';
        const current = state.currentTimeStep;
        if (current) {
          const tz = state.userTimezone || this.defaultTimeZone;

          const formatted = this.getFormatter(`label|${tz}`, {
            timeZone: tz,
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          }).format(current);

          const tzParts = this.getFormatter(`tzAbbr|${tz}`, { timeZone: tz, timeZoneName: 'short' }).formatToParts(current);
          const tzAbbr = tzParts.find(p => p.type === 'timeZoneName')?.value || '';

          let activeDailyProduct: string | null = null;
          const dailyProducts = ['aurora', 'alpenglow', 'sunrise', 'goldenhour'];
          const primStr = state.primaryProduct as string | null;

          if (primStr && (dailyProducts.includes(primStr) || primStr === 'golden_hour' || primStr === 'alpenglow_forecast')) {
            activeDailyProduct = primStr === 'golden_hour' ? 'sunrise' : (primStr === 'alpenglow_forecast' ? 'alpenglow' : primStr);
          } else {
            for (const p of dailyProducts) {
              if (
                (state.activeLayerProducts && state.activeLayerProducts[p] === true) ||
                (state as any)[`${p}Enabled`] === true
              ) {
                activeDailyProduct = p === 'golden_hour' ? 'sunrise' : (p === 'alpenglow_forecast' ? 'alpenglow' : p);
                break;
              }
            }
          }

          if (activeDailyProduct) {
            if (activeDailyProduct === 'aurora') {
              const targetDateStr = this.getAuroraTargetDateForTime(current, tz);
              const kpItem = this.getKpForTime(state.noaaKpForecast, current);
              const isAvailable = (Array.isArray(state.auroraAvailableDates) && state.auroraAvailableDates.length > 0)
                ? state.auroraAvailableDates.includes(targetDateStr)
                : (kpItem !== null);
              badgeText = this.formatForecastEventDescription('aurora', targetDateStr, kpItem ? kpItem.kp : null, isAvailable);
            } else if (state.webcamsEnabled && state.selectedWebcamDate) {
              const dateStr = state.selectedWebcamDate;
              let isMorning = true;
              try {
                const dFmt = this.getFormatter(`hour24|${tz}`, { timeZone: tz, hour: 'numeric', hour12: false });
                const localHour = parseInt(dFmt.format(current), 10);
                isMorning = (localHour === 24 ? 0 : localHour) < 12;
              } catch {
                isMorning = current.getUTCHours() < 12;
              }
              let eventStr: string;
              if ((activeDailyProduct === 'alpenglow' && state.alpenglowMode === 'goldenhour') || activeDailyProduct === 'goldenhour') {
                eventStr = isMorning ? 'goldenhour_morning' : 'goldenhour_evening';
              } else if (activeDailyProduct === 'alpenglow') {
                eventStr = isMorning ? 'alpenglow_morning' : 'alpenglow_evening';
              } else {
                eventStr = isMorning ? 'sunrise' : 'sunset';
              }
              badgeText = this.formatForecastEventDescription(eventStr, dateStr);
            } else {
              const targetProduct = (activeDailyProduct === 'alpenglow' && state.alpenglowMode === 'goldenhour')
                ? 'goldenhour'
                : (activeDailyProduct === 'goldenhour' ? 'goldenhour' : activeDailyProduct);
              const defaultModel = 'gw_v2_blend_50';
              const model = state.selectedModels[targetProduct] || state.selectedModels[activeDailyProduct] || defaultModel;
              const frame = state.manifest
                ? (ManifestTimelineHelper.getClampedFrame(state.manifest, targetProduct, model, current)
                   || ManifestTimelineHelper.getClampedFrame(state.manifest, targetProduct, defaultModel, current)
                   || ManifestTimelineHelper.getClampedFrame(state.manifest, 'goldenhour', 'gw_v2_blend_50', current)
                   || ManifestTimelineHelper.getClampedFrame(state.manifest, 'alpenglow', 'gw_v2_blend_50', current))
                : null;
              if (frame) {
                badgeText = this.formatForecastEventDescription(frame.hourStr, frame.dateStr);
              }
            }
          }

          if (!badgeText && state.manifest) {
            // If in Photography persona or no primary product is active, show the active celestial sunrise/sunset event for the day
            const isCelestialRelevant = state.activePersona === 'photography' ||
              state.activeLayerProducts?.['sunrise'] ||
              state.activeLayerProducts?.['golden_hour'] ||
              state.activeLayerProducts?.['alpenglow'] ||
              state.activeLayerProducts?.['alpenglow_forecast'] ||
              !state.primaryProduct;

            if (isCelestialRelevant) {
              const isAlpen = state.activeLayerProducts?.['alpenglow'] || state.activeLayerProducts?.['alpenglow_forecast'];
              const prod = isAlpen ? (state.alpenglowMode === 'goldenhour' ? 'goldenhour' : 'alpenglow') : 'goldenhour';
              const model = 'gw_v2_blend_50';
              const frame = ManifestTimelineHelper.getClampedFrame(state.manifest, prod, model, current);
              if (frame) {
                badgeText = this.formatForecastEventDescription(frame.hourStr, frame.dateStr);
              }
            }
          }

          // Binary search for closest timeline step (O(log N) instead of O(N) full-range scan)
          const targetMs = current.getTime();
          let low = 0;
          let high = state.timelineRange.length - 1;
          while (low <= high) {
            const mid = (low + high) >> 1;
            const midMs = this.getStepDate(state.timelineRange[mid]).getTime();
            if (midMs <= targetMs) {
              low = mid + 1;
            } else {
              high = mid - 1;
            }
          }
          const idxA = Math.max(0, Math.min(state.timelineRange.length - 1, high));
          const idxB = Math.min(state.timelineRange.length - 1, idxA + 1);
          const diffA = Math.abs(this.getStepDate(state.timelineRange[idxA]).getTime() - targetMs);
          const diffB = Math.abs(this.getStepDate(state.timelineRange[idxB]).getTime() - targetMs);
          const bestIdx = diffB < diffA ? idxB : idxA;
          const minDiff = Math.min(diffA, diffB);

          if (!badgeText && minDiff < 30 * 60 * 1000) {
            const activeStep = state.timelineRange[bestIdx];
            if (activeStep) {
              badgeText = this.formatForecastEventDescription(activeStep.hour, activeStep.date);
            }
          }

          const nextLabelText = tzAbbr ? `${formatted} (${tzAbbr})` : formatted;
          if (nextLabelText !== this.lastTimeLabelText) {
            this.lastTimeLabelText = nextLabelText;
            this.timeLabel.textContent = nextLabelText;
          }

          if (this.eventBadge && this.eventBadge.style.display !== 'none') {
            // Keep top event badge hidden since event is now rendered next to the forecast availability line
            this.eventBadge.style.display = 'none';
          }
          
          // Match slider position to closest step in timelineRange (or continuous playhead while animating)
          if (state.forecastAnimationPlaying) {
            if (this.slider.step !== '0.01') {
              this.slider.step = '0.01';
            }
            const animCursorMs = ForecastAnimationService.getInstance().getCurrentCursorMs();
            if (animCursorMs !== null) {
              this.updateContinuousPlayhead(animCursorMs);
            }
          } else if (state.liveWeatherRadarPlaying || state.liveWeatherSatellitePlaying) {
            this.updateContinuousPlayhead(targetMs);
          } else {
            if (this.slider.step !== '1') {
              this.slider.step = '1';
            }
            const nextVal = String(bestIdx);
            if (this.slider.value !== nextVal) {
              this.slider.value = nextVal;
            }
          }
        }

        this.renderScaleBar(state);
        this.renderAvailabilityBars(state, badgeText);
        this.renderSolarLunarTrack(state);
      }

      this.renderForecastControls(state);
    });
  }

  private updateContinuousPlayhead(playheadTimeMs: number): void {
    const state = store.getState();
    const timelineRange = state.timelineRange;
    if (!timelineRange || timelineRange.length < 2) return;

    if (this.slider.step !== '0.01') {
      this.slider.step = '0.01';
    }

    const firstStepMs = this.getStepDate(timelineRange[0]).getTime();
    const lastIdx = timelineRange.length - 1;
    const lastStepMs = this.getStepDate(timelineRange[lastIdx]).getTime();

    if (playheadTimeMs <= firstStepMs) {
      this.slider.value = '0';
      return;
    }
    if (playheadTimeMs >= lastStepMs) {
      this.slider.value = String(lastIdx);
      return;
    }

    let low = 0;
    let high = lastIdx;
    while (low <= high) {
      const mid = (low + high) >> 1;
      const midMs = this.getStepDate(timelineRange[mid]).getTime();
      if (midMs <= playheadTimeMs) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    const idxA = Math.max(0, high);
    const idxB = Math.min(lastIdx, idxA + 1);
    const msA = this.getStepDate(timelineRange[idxA]).getTime();
    const msB = this.getStepDate(timelineRange[idxB]).getTime();
    const fraction = msB > msA ? (playheadTimeMs - msA) / (msB - msA) : 0;
    const continuousIdx = Math.max(0, Math.min(lastIdx, idxA + fraction));
    this.slider.value = continuousIdx.toFixed(2);
  }

  private resolvePlaybackMode(state: any): 'observed' | 'forecast' | null {
    const animService = ForecastAnimationService.getInstance();
    const hasForecast = animService.hasActiveForecastLayers(state);
    const hasObserved = Boolean(
      state.liveWeatherRadarEnabled ||
      state.liveWeatherRadarLightningEnabled ||
      state.liveWeatherSatelliteEnabled
    );

    if (!hasForecast && !hasObserved) return null;
    if (hasObserved && !hasForecast) return 'observed';
    if (hasForecast && !hasObserved) return 'forecast';

    // When both Observed and Forecast layers are enabled:
    // 1. Active playback/buffering determines the active mode
    if (state.liveWeatherObservedBuffering || state.liveWeatherRadarPlaying || state.liveWeatherSatellitePlaying) {
      return 'observed';
    }
    if (state.forecastAnimationBuffering || state.forecastAnimationPlaying) {
      return 'forecast';
    }

    // 2. Otherwise follow activeDockPlaybackMode (updated on layer activation and manual timeline scrubbing)
    return state.activeDockPlaybackMode === 'observed' ? 'observed' : 'forecast';
  }

  private getObservedDurationOptions(): number[] {
    const opts = typeof __APP_CONFIG__ !== 'undefined' ? __APP_CONFIG__?.live_weather?.observed_duration_options : null;
    return Array.isArray(opts) && opts.length > 0 ? opts : [2, 4, 8];
  }

  private getForecastDurationOptions(): number[] {
    const opts = typeof __APP_CONFIG__ !== 'undefined' ? __APP_CONFIG__?.forecast_animation?.duration_options : null;
    return Array.isArray(opts) && opts.length > 0 ? opts : [12, 24, 48];
  }

  private coerceDurationOption(raw: number, opts: number[], fallback: number): number {
    const val = Number(raw) || fallback;
    if (opts.includes(val)) return val;
    return opts.reduce((best, cand) => (Math.abs(cand - val) < Math.abs(best - val) ? cand : best), opts[0] || fallback);
  }

  private renderForecastControls(state: any): void {
    if (!this.forecastToolbar) return;

    const animService = ForecastAnimationService.getInstance();
    const hasForecast = animService.hasActiveForecastLayers(state);
    const hasRadar = Boolean(state.liveWeatherRadarEnabled || state.liveWeatherRadarLightningEnabled);
    const hasSat = Boolean(state.liveWeatherSatelliteEnabled);
    const hasObserved = hasRadar || hasSat;
    const liveWeather = (window as any).liveWeatherLayer;

    // Ensure stopped playback whenever its corresponding layer family has been completely deactivated
    if (!hasForecast && (state.forecastAnimationPlaying || state.forecastAnimationBuffering)) {
      animService.stop();
    }
    if (!hasObserved && (state.liveWeatherRadarPlaying || state.liveWeatherSatellitePlaying || state.liveWeatherObservedBuffering)) {
      if (liveWeather && typeof liveWeather.stopObservedAnimation === 'function') {
        liveWeather.stopObservedAnimation(true);
      } else {
        store.update({
          liveWeatherRadarPlaying: false,
          liveWeatherSatellitePlaying: false,
          liveWeatherObservedBuffering: false
        });
      }
    }

    const mode = this.resolvePlaybackMode(state);
    if (!mode) {
      this.forecastToolbar.style.display = 'none';
      if (this.loopBracket) this.loopBracket.style.display = 'none';
      return;
    }

    this.forecastToolbar.style.display = 'flex';

    if (this.forecastPlayBtn) {
      const isBuffering = Boolean(state.forecastAnimationBuffering || state.liveWeatherObservedBuffering);
      const isPlaying = Boolean(
        state.forecastAnimationPlaying ||
        state.liveWeatherRadarPlaying ||
        state.liveWeatherSatellitePlaying
      );
      const desiredClass = isBuffering
        ? 'forecast-play-btn buffering'
        : isPlaying
          ? 'forecast-play-btn playing'
          : 'forecast-play-btn';

      this.forecastPlayBtn.title =
        mode === 'observed' ? 'Toggle observed weather loop playback' : 'Toggle forecast animation playback';

      if (this.forecastPlayBtn.className !== desiredClass) {
        this.forecastPlayBtn.className = desiredClass;
        if (isBuffering) {
          this.forecastPlayBtn.innerHTML = '<span class="play-icon">⏳</span><span class="play-text">Buffering...</span>';
        } else if (isPlaying) {
          this.forecastPlayBtn.innerHTML = '<span class="play-icon">⏹</span><span class="play-text">Stop</span>';
        } else {
          this.forecastPlayBtn.innerHTML = '<span class="play-icon">▶</span><span class="play-text">Play</span>';
        }
      }
    }

    // Dynamically switch loop duration options by layer type: Observed (2h/4h/8h) vs Forecast (12h/24h/48h)
    const durationOpts = mode === 'observed' ? this.getObservedDurationOptions() : this.getForecastDurationOptions();
    const rawDuration =
      mode === 'observed'
        ? (hasRadar ? state.liveWeatherRadarLoopHours : state.liveWeatherSatelliteLoopHours) || 8
        : state.forecastAnimationDurationHours || 12;
    const currentDuration = this.coerceDurationOption(
      rawDuration,
      durationOpts,
      mode === 'observed' ? 8 : 12
    );

    // Sync coerced duration back to store if stale
    if (mode === 'observed' && rawDuration !== currentDuration) {
      queueMicrotask(() => {
        const s = store.getState();
        if (s.liveWeatherRadarLoopHours !== currentDuration || s.liveWeatherSatelliteLoopHours !== currentDuration) {
          store.update({
            liveWeatherRadarLoopHours: currentDuration,
            liveWeatherSatelliteLoopHours: currentDuration
          });
        }
      });
    } else if (mode === 'forecast' && rawDuration !== currentDuration) {
      queueMicrotask(() => {
        if (store.getState().forecastAnimationDurationHours !== currentDuration) {
          store.update({ forecastAnimationDurationHours: currentDuration });
        }
      });
    }

    this.durationButtons.forEach((btn, idx) => {
      const optHours = durationOpts[idx] ?? durationOpts[durationOpts.length - 1];
      const el = btn as HTMLButtonElement;
      const desiredHoursStr = String(optHours);
      const desiredLabel = `${optHours}h`;
      if (el.dataset.hours !== desiredHoursStr) {
        el.dataset.hours = desiredHoursStr;
      }
      if (el.textContent !== desiredLabel) {
        el.textContent = desiredLabel;
      }
      const isSelected = optHours === currentDuration;
      el.classList.toggle('active', isSelected);
      el.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    // Update speed button active states & aria-pressed
    const currentSpeed =
      mode === 'observed'
        ? (hasRadar ? state.liveWeatherRadarPlaybackSpeed : state.liveWeatherSatPlaybackSpeed) || 1.0
        : state.forecastAnimationSpeed || 2.0;

    this.speedButtons.forEach(btn => {
      const el = btn as HTMLButtonElement;
      const s = parseFloat(el.dataset.speed || '2.0');
      const isSelected = Math.abs(s - currentSpeed) < 0.05;
      el.classList.toggle('active', isSelected);
      el.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.renderLoopBracket(state);
  }

  private renderLoopBracket(state: any): void {
    if (!this.loopBracket) return;

    const isPlaying =
      state.forecastAnimationPlaying ||
      state.liveWeatherRadarPlaying ||
      state.liveWeatherSatellitePlaying;
    if (!isPlaying || !state.timelineRange || state.timelineRange.length < 2) {
      this.loopBracket.style.display = 'none';
      return;
    }

    let startMs: number;
    let endMs: number;

    if (state.liveWeatherRadarPlaying || state.liveWeatherSatellitePlaying) {
      const obsOpts = this.getObservedDurationOptions();
      const rawH = state.liveWeatherRadarPlaying
        ? state.liveWeatherRadarLoopHours
        : state.liveWeatherSatelliteLoopHours;
      const durationHours = this.coerceDurationOption(rawH || 8, obsOpts, 8);
      const nowDate = ManifestTimelineHelper.getNowDate(state.timelineRange);
      endMs = nowDate.getTime();
      startMs = endMs - durationHours * 3600 * 1000;
    } else {
      const start = state.forecastAnimationStartTime || state.currentTimeStep;
      if (!start) {
        this.loopBracket.style.display = 'none';
        return;
      }
      const fcstOpts = this.getForecastDurationOptions();
      const durationHours = this.coerceDurationOption(
        state.forecastAnimationDurationHours || 12,
        fcstOpts,
        12
      );
      startMs = start.getTime();
      endMs = startMs + durationHours * 3600 * 1000;
    }

    let startIdx = 0;
    let endIdx = state.timelineRange.length - 1;
    let minStartDiff = Infinity;
    let minEndDiff = Infinity;

    state.timelineRange.forEach((t: any, i: number) => {
      const stepMs = this.getStepDate(t).getTime();
      const sDiff = Math.abs(stepMs - startMs);
      if (sDiff < minStartDiff) {
        minStartDiff = sDiff;
        startIdx = i;
      }
      const eDiff = Math.abs(stepMs - endMs);
      if (eDiff < minEndDiff) {
        minEndDiff = eDiff;
        endIdx = i;
      }
    });

    const totalSteps = state.timelineRange.length - 1;
    if (totalSteps <= 0) return;

    const leftPct = (Math.min(startIdx, endIdx) / totalSteps) * 100;
    const rightPct = (Math.max(startIdx, endIdx) / totalSteps) * 100;
    const widthPct = Math.max(0.5, rightPct - leftPct);

    this.loopBracket.style.left = `${leftPct}%`;
    this.loopBracket.style.width = `${widthPct}%`;
    this.loopBracket.style.display = 'block';
  }

  private getStepPercentage(idx: number, _nowIndex: number = 24, totalSteps: number = 192): number {
    // Linear mapping: each step gets equal width, matching the HTML range slider's native behavior.
    return (idx / totalSteps) * 100;
  }

  private getTimePercentage(
    timeMs: number,
    startMs: number,
    nowMs: number,
    endMs: number,
    totalSteps: number = 192,
    nowIndex: number = 24,
    range?: any[]
  ): number {
    if (totalSteps <= 0) return 0;

    const timelineRange = range || store.getState().timelineRange;
    if (timelineRange && timelineRange.length > 1) {
      const firstStepMs = this.getStepDate(timelineRange[0]).getTime();
      const lastStepMs = this.getStepDate(timelineRange[timelineRange.length - 1]).getTime();

      if (timeMs <= firstStepMs) return 0;
      if (timeMs >= lastStepMs) return 100;

      let low = 0;
      let high = timelineRange.length - 1;
      while (low <= high) {
        const mid = (low + high) >> 1;
        const midMs = this.getStepDate(timelineRange[mid]).getTime();
        if (midMs <= timeMs) {
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }
      const idxA = Math.max(0, high);
      const idxB = Math.min(timelineRange.length - 1, idxA + 1);
      if (idxA === idxB) {
        return (idxA / (timelineRange.length - 1)) * 100;
      }
      const msA = this.getStepDate(timelineRange[idxA]).getTime();
      const msB = this.getStepDate(timelineRange[idxB]).getTime();
      const fraction = (msB > msA) ? (timeMs - msA) / (msB - msA) : 0;
      const continuousIdx = idxA + fraction;
      return Math.max(0, Math.min(100, (continuousIdx / (timelineRange.length - 1)) * 100));
    }

    if (timeMs <= nowMs) {
      if (nowMs <= startMs) return (nowIndex / totalSteps) * 100;
      const fractionInPast = Math.max(0, (timeMs - startMs) / (nowMs - startMs));
      const stepIndex = fractionInPast * nowIndex;
      return Math.max(0, Math.min(100, (stepIndex / totalSteps) * 100));
    } else {
      if (endMs <= nowMs) return 100;
      const fractionInFuture = (timeMs - nowMs) / (endMs - nowMs);
      const stepIndex = nowIndex + fractionInFuture * (totalSteps - nowIndex);
      return Math.max(0, Math.min(100, (stepIndex / totalSteps) * 100));
    }
  }

  private async saveCurrentLayout(): Promise<void> {
    try {
      const ok = await LayoutManager.saveLayout();
      if (ok && this.saveLayoutBtn) {
        const textSpan = this.saveLayoutBtn.querySelector('.save-layout-text');
        if (textSpan) textSpan.textContent = '✓ Saved!';
        this.saveLayoutBtn.classList.add('saved');
        setTimeout(() => {
          if (this.saveLayoutBtn) {
            const t = this.saveLayoutBtn.querySelector('.save-layout-text');
            if (t) t.textContent = 'Save Layout';
            this.saveLayoutBtn.classList.remove('saved');
            this.saveLayoutBtn.style.display = 'none';
          }
        }, 2200);
      }
    } catch (err) {
      console.warn('Could not save layout:', err);
    }
  }

  private deactivateLayer(prod: string) {
    const state = store.getState();
    const updates: any = {};
    if (prod === 'turbulence' || prod === 'aviation_turbulence') {
      if (state.primaryProduct === 'turbulence' || state.primaryProduct === 'aviation_turbulence') {
        updates.primaryProduct = null;
      }
      if (state.activeLayerProducts) {
        updates.activeLayerProducts = {
          ...state.activeLayerProducts,
          turbulence: false,
          aviation_turbulence: false
        };
      }
      if (Array.isArray(state.activeLegendOrder)) {
        updates.activeLegendOrder = state.activeLegendOrder.filter((k: string) => k !== 'turbulence' && k !== 'aviation_turbulence');
      }
      store.update(updates);
      return;
    }
    if (state.primaryProduct === prod) {
      updates.primaryProduct = null;
    }
    if (state.activeLayerProducts && state.activeLayerProducts[prod]) {
      updates.activeLayerProducts = { ...state.activeLayerProducts, [prod]: false };
    }
    const flagMap: Record<string, string> = {
      clouds: 'cloudsEnabled',
      winds: 'windsEnabled',
      smoke: 'smokeEnabled',
      dust: 'dustEnabled',
      aurora: 'auroraEnabled',
      streamflow: 'streamflowEnabled',
      aqi: 'aqiEnabled',
      precip: 'precipEnabled',
      lightning: 'lightningEnabled',
      thunderstorm: 'thunderstormEnabled',
      lenticular: 'lenticularEnabled',
      darkskies: 'darkskiesEnabled',
      alpenglow: 'alpenglowEnabled',
      sunrise: 'sunriseEnabled',
      goldenhour: 'goldenhourEnabled',
      valley_fog: 'valleyFogEnabled',
      cloud_inversion: 'cloudInversionEnabled',
      cloud_base: 'cloudBaseEnabled',
      optics_dew_risk: 'opticsDewRiskEnabled',
      freezing_level: 'freezingLevelEnabled',
      relative_humidity: 'relativeHumidityEnabled',
      dewpoint: 'dewpointEnabled',
      windchill: 'windchillEnabled',
      cape: 'capeEnabled',
      sst: 'sstEnabled',
      wildfires: 'wildfiresEnabled',
      uv: 'liveWeatherUvEnabled',
      sun_intensity: 'sunIntensityEnabled',
      foliage: 'foliageEnabled',
      snow_depth: 'snowDepthEnabled',
      fire_restrictions: 'fireRestrictionsEnabled',
      bortle: 'bortleEnabled',
      seeing: 'seeingEnabled'
    };
    if (flagMap[prod]) {
      updates[flagMap[prod]] = false;
    }
    if (prod === 'clouds') {
      updates.cloudsEnabled = false;
    }
    if (prod === 'thunderstorm') {
      updates.thunderstormEnabled = false;
    }
    if (state.activeLayerProducts) {
      const activeLayerProducts = { ...state.activeLayerProducts };
      delete activeLayerProducts[prod];
      updates.activeLayerProducts = activeLayerProducts;
    }
    if (prod === 'live_weather') {
      updates.liveWeatherRadarEnabled = false;
      updates.liveWeatherSatelliteEnabled = false;
      updates.liveWeatherRadarLightningEnabled = false;
      updates.liveWeatherRadarPlaying = false;
      updates.liveWeatherSatellitePlaying = false;
      updates.liveWeatherObservedBuffering = false;
      const lw = (window as any).liveWeatherLayer;
      if (lw && typeof lw.stopObservedAnimation === 'function') {
        lw.stopObservedAnimation(false);
      }
    }
    store.update(updates);
  }

  public static getProductMiniGradient(prod: string, units?: 'imperial' | 'metric', model?: string): { gradient: string; minText: string; maxText: string } | null {
    const isMetric = (units || store.getState().units) === 'metric';
    switch (prod) {
      case 'hrtemp':
      case 'hrtemp_current':
      case 'live_weather_temp_gradient':
        return {
          gradient: 'linear-gradient(to right, rgb(100, 0, 150) 0%, rgb(0, 0, 255) 16.7%, rgb(0, 240, 255) 33.3%, rgb(0, 255, 0) 50%, rgb(255, 255, 0) 66.7%, rgb(255, 128, 0) 83.3%, rgb(255, 0, 0) 100%)',
          minText: isMetric ? '-20°C' : '-4°F',
          maxText: isMetric ? '40°C' : '104°F'
        };
      case 'clouds':
        return {
          gradient: 'linear-gradient(to right, rgba(210, 210, 210, 0.05) 0%, rgba(210, 210, 210, 0.4) 40%, rgba(255, 255, 255, 0.95) 100%)',
          minText: '0%',
          maxText: '100%'
        };
      case 'cloud_base':
        return {
          gradient: 'linear-gradient(to right, rgb(75, 0, 130) 0%, rgb(30, 144, 255) 20%, rgb(0, 206, 209) 35%, rgb(46, 204, 113) 50%, rgb(241, 196, 15) 70%, rgb(230, 74, 25) 85%, rgb(216, 27, 96) 100%)',
          minText: isMetric ? '0 m' : '0k',
          maxText: isMetric ? '5k+ m' : '16k+ ft'
        };
      case 'cloud_inversion':
        return {
          gradient: 'linear-gradient(to right, rgba(225, 238, 250, 0.2) 0%, rgba(235, 245, 255, 0.6) 50%, rgba(255, 255, 255, 0.95) 100%)',
          minText: 'Shallow',
          maxText: 'Deep'
        };
      case 'winds':
      case 'surface_winds':
      case 'winds_surface_current':
      case 'rtma_winds':
        return {
          gradient: 'linear-gradient(to right, rgb(0, 80, 255), rgb(34, 197, 94), rgb(234, 179, 8), rgb(249, 115, 22), rgb(239, 68, 68), rgb(217, 70, 239))',
          minText: isMetric ? '0 km/h' : '0kt',
          maxText: isMetric ? '95+ km/h' : '50+ kt'
        };
      case 'precip': {
        const pMode = store.getState().precipViewMode || 'hourly';
        if (pMode === '24h_snow') {
          return {
            gradient: 'linear-gradient(to right, #90caf9 0%, #1976d2 25%, #7b1fa2 50%, #c2185b 75%, #f57c00 90%, #ffffff 100%)',
            minText: isMetric ? '0.2 cm' : '0.1"',
            maxText: isMetric ? '60 cm+' : '24"+'
          };
        }
        if (pMode === '24h_rain' || pMode === '24h') {
          return {
            gradient: 'linear-gradient(to right, #6ee7b7 0%, #3b82f6 25%, #1d4ed8 50%, #7c3aed 75%, #ec4899 100%)',
            minText: isMetric ? '0.5 mm' : '0.02"',
            maxText: isMetric ? '50 mm+' : '2.0"+'
          };
        }
        if (pMode === 'prob') {
          return {
            gradient: 'linear-gradient(to right, #a7f3d0 0%, #0284c7 35%, #4f46e5 70%, #6d28d9 100%)',
            minText: '10%',
            maxText: '85%+'
          };
        }
        return {
          gradient: 'linear-gradient(to right, #60a5fa 0%, #2563eb 50%, #9333ea 100%)',
          minText: isMetric ? '0.2 mm' : '0.01"',
          maxText: isMetric ? '10 mm+' : '0.40"+'
        };
      }
      case 'precip_prob':
        return {
          gradient: 'linear-gradient(to right, #a7f3d0 0%, #0284c7 35%, #4f46e5 70%, #6d28d9 100%)',
          minText: '10%',
          maxText: '85%+'
        };
      case 'precip_24h':
      case 'precip_accum':
      case 'rain_accum':
      case 'live_weather_qpe':
        return {
          gradient: 'linear-gradient(to right, #6ee7b7 0%, #3b82f6 25%, #1d4ed8 50%, #7c3aed 75%, #ec4899 100%)',
          minText: isMetric ? '0.5 mm' : '0.02"',
          maxText: isMetric ? '50 mm+' : '2.0"+'
        };
      case 'snow_24h':
      case 'snow_accum':
      case 'live_weather_snow':
        return {
          gradient: 'linear-gradient(to right, #90caf9 0%, #1976d2 25%, #7b1fa2 50%, #c2185b 75%, #f57c00 90%, #ffffff 100%)',
          minText: isMetric ? '0.2 cm' : '0.1"',
          maxText: isMetric ? '60 cm+' : '24"+'
        };
      case 'snow_depth':
      case 'snow_stations':
        return {
          gradient: 'linear-gradient(to right, #38bdf8 0%, #1d4ed8 25%, #4338ca 50%, #7e22ce 75%, #f472b6 100%)',
          minText: isMetric ? '1 cm' : '0.5"',
          maxText: isMetric ? '300 cm+' : '120"+'
        };
      case 'smoke':
        return {
          gradient: 'linear-gradient(to right, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.9) 100%)',
          minText: 'Clear',
          maxText: '300 ug'
        };
      case 'dust':
      case 'dust_forecast':
        return {
          gradient: 'linear-gradient(to right, rgba(217, 119, 6, 0.05) 0%, rgba(217, 119, 6, 0.9) 100%)',
          minText: 'Clear',
          maxText: '100+ ug'
        };
      case 'aqi':
      case 'aqi_stations': {
        const state = store.getState();
        const aqiModel = (model || state.selectedModels?.['aqi'] || 'gefs').toLowerCase();
        const isAqhiMode = (aqiModel === 'raqdps' || state.aqiMode === 'aqhi') && prod !== 'aqi_stations';
        if (isAqhiMode) {
          return {
            gradient: 'linear-gradient(to right, rgb(0, 180, 255) 0%, rgb(240, 210, 0) 35%, rgb(255, 130, 0) 65%, rgb(200, 0, 0) 100%)',
            minText: '1 (Low)',
            maxText: '10+ (V.High)'
          };
        }
        return {
          gradient: 'linear-gradient(to right, #00e400 0%, #ffff00 20%, #ff7e00 40%, #ff0000 60%, #8f3f97 80%, #7e0023 100%)',
          minText: 'Good',
          maxText: 'Haz (300+)'
        };
      }
      case 'relative_humidity':
        return {
          gradient: 'linear-gradient(to right, #d7ccc8 0%, #fbc02d 30%, #4dd0e1 60%, #0d47a1 100%)',
          minText: '0%',
          maxText: '100%'
        };
      case 'dewpoint':
        return {
          gradient: 'linear-gradient(to right, #82a5d7 0%, #64c3d7 20%, #46cd73 40%, #f0d71e 60%, #fa8c14 75%, #eb1e28 90%, #8c0082 100%)',
          minText: isMetric ? '-30°C' : '-20°F',
          maxText: isMetric ? '30°C' : '85°F'
        };
      case 'freezing_level':
      case 'freezing_level_contours':
        return {
          gradient: 'linear-gradient(to right, #8b5cf6 0%, #3b82f6 16.6%, #06b6d4 33.3%, #10b981 50%, #eab308 66.6%, #f97316 83.3%, #ef4444 100%)',
          minText: isMetric ? '0 m' : '0ft',
          maxText: isMetric ? '5.5k+ m' : '18k+ ft'
        };
      case 'hrtemp_contours':
        return {
          gradient: 'linear-gradient(to right, #7c3aed 0%, #2563eb 15%, #0ea5e9 25%, #00f0ff 32%, #10b981 40%, #22c55e 50%, #eab308 60%, #f97316 70%, #ef4444 80%, #b91c1c 95%)',
          minText: isMetric ? '-18°C' : '0°F',
          maxText: isMetric ? '35°C+' : '95°F+'
        };
      case 'seeing':
        return {
          gradient: 'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #10b981 50%, #06b6d4 75%, #2563eb 100%)',
          minText: 'Poor (0)',
          maxText: 'Superb (100)'
        };
      case 'valley_fog':
        return {
          gradient: 'linear-gradient(to right, rgba(84, 110, 122, 0.3) 0%, rgba(176, 190, 197, 0.6) 50%, rgba(236, 239, 241, 0.9) 100%)',
          minText: '15%',
          maxText: '85%+'
        };
      case 'windchill':
        return {
          gradient: 'linear-gradient(to right, rgb(100, 0, 150) 0%, rgb(0, 0, 255) 20%, rgb(0, 240, 255) 40%, rgb(0, 255, 0) 60%, rgb(255, 255, 0) 80%, rgb(255, 0, 0) 100%)',
          minText: '-30°F',
          maxText: '50°F'
        };
      case 'thunderstorm':
      case 'cape':
        return {
          gradient: 'linear-gradient(to right, #00e5ff 0%, #76ff03 25%, #ffea00 50%, #ff9100 70%, #d50000 85%, #4a148c 100%)',
          minText: '15%',
          maxText: '100%'
        };
      case 'lightning':
      case 'lightning_24h':
        return {
          gradient: 'linear-gradient(to right, #9664ff 0%, #ff8000 50%, #ff0000 100%)',
          minText: '10',
          maxText: '70+'
        };
      case 'live_weather_lightning':
        return {
          gradient: 'linear-gradient(to right, #ffe800 0%, #ffc600 10%, #f59e0b 24%, #cc8a44 45%, #868c96 72%, #606c7c 100%)',
          minText: '0-5m',
          maxText: '8h'
        };
      case 'sunrise':
        return {
          gradient: 'linear-gradient(to right, #0046a0 0%, #00b4d8 25%, #ffff00 50%, #ff8000 75%, #ff0000 95%, #b71c1c 100%)',
          minText: '0',
          maxText: '90+'
        };
      case 'alpenglow':
      case 'goldenhour':
        return {
          gradient: 'linear-gradient(to right, #0046a0 0%, #00b4d8 25%, #ffff00 50%, #ff8000 75%, #ff0000 95%, #b71c1c 100%)',
          minText: 'Low',
          maxText: 'Vivid'
        };
      case 'darkskies':
        return {
          gradient: 'linear-gradient(to right, #b40000 0%, #ff8000 20%, #ffff00 40%, #00ff00 60%, #00ffff 80%, #0032ff 100%)',
          minText: 'Poor',
          maxText: 'Ideal'
        };
      case 'uv':
        return {
          gradient: 'linear-gradient(to right, #22c55e, #eab308, #f97316, #ef4444, #a855f7)',
          minText: '0',
          maxText: '11+'
        };
      case 'sun_intensity':
        return {
          gradient: 'linear-gradient(to right, #5c6bc0 0%, #c0ca33 20%, #ffb300 40%, #fb8c00 65%, #e53935 85%, #8e24aa 100%)',
          minText: '0',
          maxText: '1200+ W'
        };
      case 'optics_dew_risk':
        return {
          gradient: 'linear-gradient(to right, #22c55e 0%, #f59e0b 50%, #ef4444 100%)',
          minText: 'Low',
          maxText: 'High'
        };
      case 'lenticular':
        return {
          gradient: 'linear-gradient(to right, rgba(0, 210, 255, 0.1) 0%, rgba(0, 210, 255, 0.6) 50%, rgba(255, 255, 255, 0.95) 100%)',
          minText: 'Low',
          maxText: 'High'
        };
      case 'aurora':
        return {
          gradient: 'linear-gradient(to right, rgba(144, 238, 0, 0.4) 0%, rgba(0, 255, 0, 0.8) 33%, rgba(173, 255, 0, 0.9) 66%, rgba(255, 255, 0, 1.0) 100%)',
          minText: 'Low',
          maxText: 'Active'
        };
      case 'sst':
      case 'water_temp':
      case 'streamflow':
        return {
          gradient: 'linear-gradient(to right, #0b132b 0%, #1c2541 15%, #3a506b 30%, #5bc0be 45%, #4895ef 60%, #ff9e00 75%, #d90429 90%, #9d0208 100%)',
          minText: '32°F',
          maxText: '85°F+'
        };
      case 'live_weather_radar':
      case 'live_weather':
        return {
          gradient: 'linear-gradient(to right, #00e676 0%, #ffeb3b 40%, #ff5722 75%, #e91e63 100%)',
          minText: '15 dBZ',
          maxText: '65+ dBZ'
        };
      case 'bortle':
        return {
          gradient: 'linear-gradient(to right, #0f172a 0%, #1e3a8a 12.5%, #2563eb 25%, #06b6d4 37.5%, #22c55e 50%, #eab308 62.5%, #f97316 75%, #ef4444 87.5%, #d946ef 100%)',
          minText: 'Class 1',
          maxText: 'Class 9'
        };
      case 'foliage':
        return {
          gradient: 'linear-gradient(to right, #22c55e 0%, #84cc16 25%, #eab308 50%, #f97316 75%, #ef4444 90%, #7f1d1d 100%)',
          minText: 'Green',
          maxText: 'Past'
        };
      case 'wildfires':
      case 'wildfire_hotspots':
        return {
          gradient: 'linear-gradient(to right, rgba(239, 68, 68, 0.35) 0%, #fbbf24 40%, #f97316 70%, #ef4444 100%)',
          minText: 'Perimeter',
          maxText: 'Hotspot'
        };
      case 'avalanche':
      case 'avalanche_danger':
        return {
          gradient: 'linear-gradient(to right, #50b848 0%, #fff100 25%, #f7941e 50%, #ed1c24 75%, #231f20 100%)',
          minText: '1 (Low)',
          maxText: '5 (Ext)'
        };
      case 'turbulence':
      case 'aviation_turbulence':
        return {
          gradient: 'linear-gradient(to right, #2ecc71 0%, #f1c412 28%, #e67e22 50%, #e74c3c 72%, #f032f0 100%)',
          minText: 'Smooth',
          maxText: 'Ext'
        };
      case 'jet_stream':
        return {
          gradient: 'linear-gradient(to right, #00a8ff 0%, #0984e3 30%, #6c5ce7 55%, #e056fd 78%, #ff4757 100%)',
          minText: '45kt',
          maxText: '180+'
        };
      default:
        return null;
    }
  }

  private renderAvailabilityBars(state: any, badgeText?: string) {
    if (!this.availabilityContainer) return;

    if (state.timelineForecastCoverageEnabled === false) {
      this.availabilityContainer.innerHTML = '';
      this.availabilityContainer.style.display = 'none';
      this.lastAvailabilitySig = 'off';
      return;
    }

    const range = state.timelineRange;
    if (range.length < 2) {
      this.availabilityContainer.innerHTML = '';
      this.lastAvailabilitySig = 'empty';
      return;
    }

    const totalSteps = range.length - 1;
    const nowIndex = ManifestTimelineHelper.getNowIndex(range);
    const startMs = this.getStepDate(range[0]).getTime();
    const nowMs = range.length > nowIndex ? this.getStepDate(range[nowIndex]).getTime() : startMs;
    const endMs = this.getStepDate(range[totalSteps]).getTime();

    // Collect active products across primary product, activeLayerProducts map, and individual toggles
    const normalizeDockProd = (p: string): string => (p === 'aviation_turbulence' ? 'turbulence' : p);
    const activeProducts: string[] = [];
    if (state.primaryProduct) {
      activeProducts.push(normalizeDockProd(state.primaryProduct));
    }

    if (state.activeLayerProducts) {
      Object.keys(state.activeLayerProducts).forEach(rawProd => {
        const prod = normalizeDockProd(rawProd);
        if (state.activeLayerProducts[rawProd] && !activeProducts.includes(prod)) {
          activeProducts.push(prod);
        }
      });
    }

    const flagMap: Record<string, string> = {
      clouds: 'cloudsEnabled',
      winds: 'windsEnabled',
      smoke: 'smokeEnabled',
      dust: 'dustEnabled',
      aurora: 'auroraEnabled',
      streamflow: 'streamflowEnabled',
      aqi: 'aqiEnabled',
      precip: 'precipEnabled',
      lightning: 'lightningEnabled',
      thunderstorm: 'thunderstormEnabled',
      lenticular: 'lenticularEnabled',
      darkskies: 'darkskiesEnabled',
      alpenglow: 'alpenglowEnabled',
      sunrise: 'sunriseEnabled',
      goldenhour: 'goldenhourEnabled',
      valley_fog: 'valleyFogEnabled',
      cloud_inversion: 'cloudInversionEnabled',
      cloud_base: 'cloudBaseEnabled',
      optics_dew_risk: 'opticsDewRiskEnabled',
      freezing_level: 'freezingLevelEnabled',
      relative_humidity: 'relativeHumidityEnabled',
      dewpoint: 'dewpointEnabled',
      windchill: 'windchillEnabled',
      cape: 'capeEnabled',
      sst: 'sstEnabled',
      wildfires: 'wildfiresEnabled',
      uv: 'liveWeatherUvEnabled',
      sun_intensity: 'sunIntensityEnabled',
      foliage: 'foliageEnabled',
      snow_depth: 'snowDepthEnabled',
      fire_restrictions: 'fireRestrictionsEnabled',
      bortle: 'bortleEnabled',
      seeing: 'seeingEnabled'
    };

    Object.entries(flagMap).forEach(([prod, flagKey]) => {
      if ((state as any)[flagKey] && !activeProducts.includes(prod)) {
        activeProducts.push(prod);
      }
    });

    if (state.auroraEnabled || (state.activeLayerProducts && (state.activeLayerProducts['aurora_viewline'] || state.activeLayerProducts['aurora'] || state.activeLayerProducts['kp_index']))) {
      if (!activeProducts.includes('aurora')) activeProducts.push('aurora');
    }

    if (state.liveWeatherRadarEnabled || state.liveWeatherSatelliteEnabled) {
      if (!activeProducts.includes('live_weather')) activeProducts.push('live_weather');
    }

    if (
      state.manifest !== this.lastManifestRef ||
      state.productAvailability !== this.lastAvailabilityMapRef ||
      state.noaaKpForecast !== this.lastKpForecastRef
    ) {
      this.lastManifestRef = state.manifest;
      this.lastAvailabilityMapRef = state.productAvailability;
      this.lastKpForecastRef = state.noaaKpForecast;
      this.dataRevCounter++;
    }

    const hasSunriseProd = activeProducts.some(
      p => p === 'sunrise' || p === 'goldenhour' || p === 'golden_hour' || p === 'alpenglow'
    );
    const modelsKey = state.selectedModels
      ? Object.entries(state.selectedModels)
          .map(([k, v]) => `${k}:${v}`)
          .join('|')
      : '';
    const availabilitySig = [
      this.dataRevCounter,
      range.length,
      startMs,
      nowMs,
      endMs,
      nowIndex,
      activeProducts.join(','),
      state.primaryProduct || '',
      state.lastActivatedProduct || '',
      state.precipViewMode || 'hourly',
      (state as any).hrtempRasterEnabled === false ? '0' : '1',
      state.cloudModel || '',
      Array.isArray(state.cloudModels) ? state.cloudModels.join(',') : '',
      state.smokeModel || '',
      state.sunIntensityModel || '',
      modelsKey,
      hasSunriseProd
        ? `${badgeText || ''}|${state.alpenglowMode || ''}|${state.currentTimeStep ? Math.floor(state.currentTimeStep.getTime() / (3600 * 1000)) : ''}`
        : ''
    ].join('~');

    if (availabilitySig === this.lastAvailabilitySig) {
      if (this.availabilityContainer.style.display !== 'flex') {
        this.availabilityContainer.style.display = 'flex';
      }
      return;
    }
    this.lastAvailabilitySig = availabilitySig;
    this.availabilityContainer.style.display = 'flex';
    this.availabilityContainer.innerHTML = '';

    activeProducts.forEach(prod => {
      // If base temperature raster is disabled and contours are active, suppress base hrtemp raster bar
      if (prod === 'hrtemp' && (state as any).hrtempRasterEnabled === false && (state.activeLayerProducts && state.activeLayerProducts['hrtemp_contours'])) {
        return;
      }

      // Determine the active model for this product
      let queryProd = prod;
      if (prod === 'precip') {
        const pMode = state.precipViewMode || 'hourly';
        if (pMode === '24h_snow') queryProd = 'snow_24h';
        else if (pMode === '24h_rain' || pMode === '24h') queryProd = 'precip_24h';
        else if (pMode === 'prob') queryProd = 'precip_prob';
      }

      let selectedModel: string | undefined = state.selectedModels?.[queryProd] || state.selectedModels?.[prod];
      if (prod === 'clouds') {
        selectedModel = (state.cloudModels && state.cloudModels[0]) || state.cloudModel || selectedModel;
      } else if (prod === 'smoke') {
        selectedModel = state.smokeModel || selectedModel;
      } else if (prod === 'dust' || prod === 'dust_forecast') {
        selectedModel = 'gefs_dust';
      } else if (prod === 'sun_intensity') {
        selectedModel = state.sunIntensityModel || selectedModel;
      } else if (prod === 'hrtemp_contours') {
        selectedModel = selectedModel || 'nbm';
      } else if (prod === 'hrtemp_current') {
        selectedModel = 'rtma';
      } else if (prod === 'seeing') {
        selectedModel = selectedModel || 'rdps';
      } else if (prod === 'turbulence') {
        selectedModel = selectedModel || state.selectedModels?.['aviation_turbulence'];
        const abm = (state.manifest as any)?.turbulence?.available_by_model || {};
        const exposed: string[] = state.manifest?.exposed_models?.turbulence || ['dafs', 'gfs', 'ecmwf', 'hgefs', 'gdps', 'icon13'];
        const hasFrames = (mKey?: string) => !!(mKey && abm[mKey] && Object.keys(abm[mKey]).length > 0);
        if (!selectedModel || (Object.keys(abm).length > 0 && !hasFrames(selectedModel))) {
          selectedModel = exposed.find(hasFrames) || Object.keys(abm).find(hasFrames) || selectedModel || exposed[0] || 'gfs';
        }
      }

      // Check if fresh bounds should be computed from manifest for the active model
      let bounds = state.productAvailability?.[queryProd] || state.productAvailability?.[prod];
      if (state.manifest) {
        const queryModel = selectedModel || (state.manifest.exposed_models?.[queryProd]?.[0]) || (state.manifest.exposed_models?.[prod]?.[0]) || 'default';
        const freshBounds = ManifestTimelineHelper.getProductBounds(state.manifest, queryProd, queryModel);
        if (freshBounds) {
          bounds = freshBounds;
        }
      }

      // Aurora & Kp-Index are ephemeris/geomagnetic forecasts without raster map tiles
      if ((prod === 'aurora' || prod === 'kp_index') && (!bounds || !bounds.minTime || !bounds.maxTime)) {
        if (Array.isArray(state.noaaKpForecast) && state.noaaKpForecast.length > 0) {
          const firstItem = state.noaaKpForecast[0];
          const lastItem = state.noaaKpForecast[state.noaaKpForecast.length - 1];
          const minD = new Date(firstItem.time_tag.endsWith('Z') ? firstItem.time_tag : firstItem.time_tag + 'Z');
          const maxD = new Date(lastItem.time_tag.endsWith('Z') ? lastItem.time_tag : lastItem.time_tag + 'Z');
          bounds = {
            minTime: isNaN(minD.getTime()) ? new Date(startMs) : minD,
            maxTime: isNaN(maxD.getTime()) ? new Date(endMs) : maxD,
            color: '#a855f7',
            label: '🌌 NOAA Kp-Index',
            type: 'forecast'
          };
        }
      }

      let isPendingCycleSlices = false;
      if (!bounds || !bounds.minTime || !bounds.maxTime) {
        const prodMeta = PRODUCT_META[queryProd] || PRODUCT_META[prod];
        const candidateModels = state.manifest ? ManifestTimelineHelper.getAvailableModelsForProduct(state.manifest, queryProd) : [];
        if (prodMeta && prodMeta.type === 'forecast' && candidateModels.length > 0) {
          isPendingCycleSlices = true;
          const fallbackHorizonHours = queryProd === 'turbulence' || queryProd === 'jet_stream' ? 72 : 120;
          bounds = {
            product: queryProd,
            minTime: new Date(nowMs),
            maxTime: new Date(Math.min(endMs, nowMs + fallbackHorizonHours * 3600 * 1000)),
            frames: [],
            color: prodMeta.color,
            icon: prodMeta.icon,
            label: prodMeta.label,
            type: 'forecast'
          };
        } else {
          return;
        }
      }

      const minMs = bounds.minTime.getTime();
      const isSunriseProd = (prod === 'sunrise' || prod === 'goldenhour' || prod === 'golden_hour' || prod === 'alpenglow');
      const isNowcastModel = isSunriseProd && String(selectedModel || '').toLowerCase() === 'gw_nowcast';
      const maxMs = isNowcastModel
        ? Math.max(bounds.maxTime.getTime(), nowMs + 6 * 3600 * 1000)
        : bounds.maxTime.getTime();

      // Check if product is an observation or forecast product
      const isObservation = (bounds.type === 'observation') || (PRODUCT_META[prod]?.type === 'observation') || (prod === 'live_weather' || prod === 'streamflow' || prod === 'wildfires' || prod === 'fire_restrictions' || prod === 'avalanche' || prod === 'sst');

      // For forecast products, availability starts at NOW and extends out to maxTime.
      // For Sunrise/Sunset/Golden Hour/Alpenglow (including Live NowCast), anchor the label gutter at NOW
      // so the event & date badge always renders in the exact same position in the date/time dock.
      const effectiveMinMs = isObservation ? minMs : (isSunriseProd ? nowMs : Math.max(nowMs, minMs));

      // If forecast ends before NOW, do not render a stale line (except active twilight/NowCast layers)
      if (!isObservation && !isSunriseProd && maxMs < nowMs) return;

      const leftPct = this.getTimePercentage(effectiveMinMs, startMs, nowMs, endMs, totalSteps, nowIndex, range);
      const rightPct = this.getTimePercentage(maxMs, startMs, nowMs, endMs, totalSteps, nowIndex, range);
      const widthPct = Math.max(0.5, rightPct - leftPct);

      const rowEl = document.createElement('div');
      rowEl.className = 'availability-bar-row clickable-availability-row';

      // Determine canonical product for trend chart focus synchronization
      let targetCanonical = prod;
      if (targetCanonical === 'hrtemp_contours' || targetCanonical === 'hrtemp_current') targetCanonical = 'hrtemp';
      if (targetCanonical === 'winds_surface_current' || targetCanonical === 'rtma_winds') targetCanonical = 'winds';
      if (targetCanonical === 'aviation_turbulence') targetCanonical = 'turbulence';
      if (targetCanonical === 'rain_accum' || targetCanonical === 'precip_accum') targetCanonical = 'precip_accum';
      if (targetCanonical === 'snow_accum') targetCanonical = 'snow_accum';
      if (targetCanonical === 'live_weather_radar') targetCanonical = 'precip';

      let currentActiveProd = state.lastActivatedProduct || state.primaryProduct;
      if (currentActiveProd === 'hrtemp_current' || currentActiveProd === 'hrtemp_contours') currentActiveProd = 'hrtemp';
      if (currentActiveProd === 'winds_surface_current' || currentActiveProd === 'rtma_winds') currentActiveProd = 'winds';
      if (currentActiveProd === 'aviation_turbulence') currentActiveProd = 'turbulence';
      if (currentActiveProd === 'rain_accum') currentActiveProd = 'precip_accum';

      const isFocused = (currentActiveProd === targetCanonical || currentActiveProd === prod);
      if (isFocused) {
        rowEl.classList.add('active-availability-row');
      }

      rowEl.title = `Click to focus ${bounds.label || prod} in trend chart`;
      rowEl.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (
          target.closest('.timeline-layer-remove-btn') ||
          target.closest('.timeline-row-model-wrap') ||
          target.closest('.timeline-row-model-select') ||
          target.closest('.timeline-nowcast-mode-pill') ||
          target.closest('.kp-timeline-pill') ||
          target.closest('.celestial-timeline-pill') ||
          target.closest('.timeline-event-step-btn')
        ) {
          return;
        }
        store.update({ lastActivatedProduct: targetCanonical });
      });

      // Left gutter label container
      const labelEl = document.createElement('div');
      labelEl.className = 'availability-bar-label';
      labelEl.style.position = 'absolute';
      labelEl.style.left = '0';
      labelEl.style.width = `${leftPct}%`;
      labelEl.style.height = '100%';
      labelEl.style.display = 'flex';
      labelEl.style.alignItems = 'center';
      labelEl.style.justifyContent = 'flex-end';
      labelEl.style.paddingRight = '8px';
      labelEl.style.gap = '6px';
      labelEl.style.pointerEvents = 'auto';
      labelEl.style.boxSizing = 'border-box';

      // Check available forecast models for inline model switching
      const availableModels = state.manifest ? ManifestTimelineHelper.getAvailableModelsForProduct(state.manifest, queryProd) : [];
      const hasMultipleModels = availableModels && availableModels.length > 1;

      // Determine product display label (strip redundant "Forecast" suffix to save space)
      let displayLabel = (bounds.label || prod).replace(/\s+Forecast$/i, '');
      if (prod === 'hrtemp_contours') {
        displayLabel = hasMultipleModels ? 'Temperature Contours' : `Temperature Contours (${(selectedModel || 'nbm').toUpperCase()})`;
      } else if (prod === 'precip') {
        const pMode = state.precipViewMode || 'hourly';
        const modelName = (selectedModel || 'nbm').toUpperCase();
        if (pMode === '24h_snow') {
          displayLabel = hasMultipleModels ? 'Daily Snow' : `Daily Snow (${modelName})`;
        } else if (pMode === '24h_rain' || pMode === '24h') {
          displayLabel = hasMultipleModels ? 'Daily Precip' : `Daily Precip (${modelName})`;
        } else if (pMode === 'prob') {
          displayLabel = hasMultipleModels ? 'Precip Probability' : `Precip Probability (${modelName})`;
        } else {
          displayLabel = hasMultipleModels ? 'Precip Rate' : `Precip Rate (${modelName})`;
        }
      }

      // 1. Quick Layer Removal [✕] Button
      const removeBtn = document.createElement('button');
      removeBtn.className = 'timeline-layer-remove-btn';
      removeBtn.type = 'button';
      removeBtn.title = `Remove ${displayLabel} layer`;
      removeBtn.innerHTML = '✕';
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.deactivateLayer(prod);
      });
      labelEl.appendChild(removeBtn);

      // 2. Mini Gradient Swatch (if continuous product)
      const miniGrad = Timeline.getProductMiniGradient(prod, undefined, selectedModel);
      if (miniGrad) {
        const swatchEl = document.createElement('div');
        swatchEl.className = 'timeline-mini-legend-swatch';
        swatchEl.title = `${displayLabel} scale: ${miniGrad.minText} to ${miniGrad.maxText}`;
        swatchEl.style.background = miniGrad.gradient;
        swatchEl.innerHTML = `<span class="swatch-min">${miniGrad.minText}</span><span class="swatch-max">${miniGrad.maxText}</span>`;
        labelEl.appendChild(swatchEl);
      }

      if (isSunriseProd) {
        const activeTwilightModel = String(selectedModel || 'gw_v2_blend_50').toLowerCase();
        const isNowcastActive = activeTwilightModel === 'gw_nowcast';
        const targetTwilightProd = (prod === 'alpenglow' && state.alpenglowMode === 'goldenhour')
          ? 'goldenhour'
          : (prod === 'golden_hour' ? 'goldenhour' : prod);

        let twilightFrames = state.manifest
          ? ManifestTimelineHelper.getProductFrames(state.manifest, targetTwilightProd, activeTwilightModel)
          : [];
        if ((!twilightFrames || twilightFrames.length === 0) && state.manifest) {
          twilightFrames = ManifestTimelineHelper.getProductFrames(state.manifest, targetTwilightProd, 'gw_v2_blend_50');
        }
        const sortedTwilightFrames = (twilightFrames || []).slice().sort((a, b) => a.timestamp - b.timestamp);

        let rowBadgeText = badgeText || '';
        let activeRowFrame: any = null;
        if (state.manifest && state.currentTimeStep) {
          const rowFrame = ManifestTimelineHelper.getClampedFrame(state.manifest, targetTwilightProd, activeTwilightModel, state.currentTimeStep)
            || ManifestTimelineHelper.getClampedFrame(state.manifest, targetTwilightProd, 'gw_v2_blend_50', state.currentTimeStep);
          if (rowFrame) {
            activeRowFrame = rowFrame;
            const desc = this.formatForecastEventDescription(rowFrame.hourStr, rowFrame.dateStr);
            if (desc) rowBadgeText = desc;
          }
        }
        if (rowBadgeText) {
          const nowcastToggleBtn = document.createElement('button');
          nowcastToggleBtn.type = 'button';
          nowcastToggleBtn.className = 'timeline-nowcast-mode-pill';
          nowcastToggleBtn.style.cssText = `
            padding: 1px 6px;
            border-radius: 4px;
            font-size: 0.62rem;
            font-weight: 800;
            letter-spacing: 0.02em;
            cursor: pointer;
            border: 1px solid ${isNowcastActive ? 'rgba(245, 158, 11, 0.8)' : 'rgba(56, 189, 248, 0.45)'};
            background: ${isNowcastActive ? 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)' : 'rgba(56, 189, 248, 0.16)'};
            color: ${isNowcastActive ? '#0f172a' : '#e0f2fe'};
            white-space: nowrap;
          `;
          nowcastToggleBtn.innerText = isNowcastActive ? '⚡ NOWCAST' : '📅 10-DAY';
          nowcastToggleBtn.title = isNowcastActive
            ? 'SkySkout Sunset NowCast active (Live GOES + Radar). Click to switch to 10-Day NWP Forecast.'
            : '10-Day NWP Forecast active. Click to switch to SkySkout Live NowCast (2h/4h/6h Satellite/Radar).';
          nowcastToggleBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const targetProd = (prod === 'alpenglow' && state.alpenglowMode === 'goldenhour') ? 'goldenhour' : (prod === 'golden_hour' ? 'goldenhour' : prod);
            if (isNowcastActive) {
              let restoreModel = 'gw_v2_blend_50';
              try {
                const saved = localStorage.getItem(`gw_last_nwp_model_${targetProd}`);
                if (saved && saved !== 'gw_nowcast') restoreModel = saved;
              } catch (_) {}
              setProductModel(targetProd, restoreModel, true);
              LayerFirstTimeToast.getInstance().showIfFirstTime(targetProd, restoreModel);
            } else {
              setProductModel(targetProd, 'gw_nowcast', true);
              LayerFirstTimeToast.getInstance().showIfFirstTime(targetProd, 'gw_nowcast');
            }
          });
          labelEl.appendChild(nowcastToggleBtn);

          const activeFrameIdx = activeRowFrame
            ? sortedTwilightFrames.findIndex((f) => f.key === activeRowFrame.key && f.timestamp === activeRowFrame.timestamp)
            : -1;

          if (sortedTwilightFrames.length > 1) {
            const prevBtn = document.createElement('button');
            prevBtn.type = 'button';
            prevBtn.className = 'timeline-event-step-btn';
            prevBtn.innerText = '◀';
            prevBtn.title = 'Previous event';
            prevBtn.setAttribute('aria-label', 'Previous event');
            prevBtn.disabled = activeFrameIdx <= 0;
            prevBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              if (activeFrameIdx > 0) {
                store.update({ currentTimeStep: new Date(sortedTwilightFrames[activeFrameIdx - 1].timestamp) });
              }
            });
            labelEl.appendChild(prevBtn);
          }

          const badgePill = document.createElement('span');
          badgePill.className = 'timeline-event-badge-inline';
          badgePill.innerText = rowBadgeText;
          labelEl.appendChild(badgePill);

          if (sortedTwilightFrames.length > 1) {
            const nextBtn = document.createElement('button');
            nextBtn.type = 'button';
            nextBtn.className = 'timeline-event-step-btn';
            nextBtn.innerText = '▶';
            nextBtn.title = 'Next event';
            nextBtn.setAttribute('aria-label', 'Next event');
            nextBtn.disabled = activeFrameIdx < 0 || activeFrameIdx >= sortedTwilightFrames.length - 1;
            nextBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              if (activeFrameIdx >= 0 && activeFrameIdx < sortedTwilightFrames.length - 1) {
                store.update({ currentTimeStep: new Date(sortedTwilightFrames[activeFrameIdx + 1].timestamp) });
              }
            });
            labelEl.appendChild(nextBtn);
          } else {
            const eventIcon = document.createElement('span');
            eventIcon.className = 'timeline-event-indicator-icon';
            eventIcon.innerText = '⯁';
            labelEl.appendChild(eventIcon);
          }
        }
      } else {
        const titleSpan = document.createElement('span');
        titleSpan.className = 'availability-bar-title';
        titleSpan.innerText = displayLabel;
        labelEl.appendChild(titleSpan);

        // 3. Compact Inline Model Selector for Forecast Layers
        if (hasMultipleModels) {
          const modelWrap = document.createElement('div');
          modelWrap.className = 'timeline-row-model-wrap';

          const currentModelId = selectedModel || availableModels[0]?.id || '';
          const currentModelObj = availableModels.find(m => m.id.toLowerCase() === currentModelId.toLowerCase()) || availableModels[0];
          const rawId = (currentModelObj?.id || currentModelId).toUpperCase();
          const truncatedText = rawId.length > 7 ? rawId.slice(0, 7) : rawId;

          const pillText = document.createElement('span');
          pillText.className = 'timeline-row-model-text';
          pillText.innerText = truncatedText;
          modelWrap.appendChild(pillText);

          const pillArrow = document.createElement('span');
          pillArrow.className = 'timeline-row-model-arrow';
          pillArrow.innerText = '▾';
          modelWrap.appendChild(pillArrow);

          const modelSelect = document.createElement('select');
          modelSelect.className = 'timeline-row-model-select';
          modelSelect.title = `Switch model for ${displayLabel} (Current: ${currentModelObj?.label || rawId})`;

          availableModels.forEach(m => {
            const opt = document.createElement('option');
            opt.value = m.id;
            opt.innerText = m.label || m.id.toUpperCase();
            if (m.id.toLowerCase() === currentModelId.toLowerCase()) {
              opt.selected = true;
            }
            modelSelect.appendChild(opt);
          });

          modelSelect.addEventListener('click', (e) => {
            e.stopPropagation();
          });

          modelSelect.addEventListener('change', (e) => {
            e.stopPropagation();
            const newModel = modelSelect.value;
            const newRaw = newModel.toUpperCase();
            pillText.innerText = newRaw.length > 7 ? newRaw.slice(0, 7) : newRaw;
            setProductModel(queryProd, newModel, true);
          });

          modelWrap.appendChild(modelSelect);
          labelEl.appendChild(modelWrap);
        }

        const indicatorSpan = document.createElement('span');
        indicatorSpan.className = 'availability-bar-indicator';
        indicatorSpan.innerText = isFocused ? '▶' : '▲';
        labelEl.appendChild(indicatorSpan);
      }
      rowEl.appendChild(labelEl);

      const lineEl = document.createElement('div');
      lineEl.className = 'availability-line';
      lineEl.style.left = `${leftPct}%`;
      lineEl.style.width = `${widthPct}%`;
      if (isPendingCycleSlices) {
        lineEl.style.backgroundColor = 'transparent';
        lineEl.style.borderTop = `2px dashed ${bounds.color || '#38bdf8'}`;
        lineEl.style.opacity = '0.5';
        lineEl.title = `${bounds.label}: Point forecast trend telemetry active (raster map slices updating for current cycle)`;
      } else {
        lineEl.style.backgroundColor = bounds.color || '#38bdf8';
        lineEl.title = `${bounds.label}: ${new Date(effectiveMinMs).toISOString().slice(0, 16).replace('T', ' ')} to ${bounds.maxTime.toISOString().slice(0, 16).replace('T', ' ')}`;
      }
      rowEl.appendChild(lineEl);

      if (isSunriseProd && state.manifest) {
        const activeTwilightModel = String(selectedModel || 'gw_v2_blend_50').toLowerCase();
        const targetTwilightProd = (prod === 'alpenglow' && state.alpenglowMode === 'goldenhour')
          ? 'goldenhour'
          : (prod === 'golden_hour' ? 'goldenhour' : prod);
        let frames = ManifestTimelineHelper.getProductFrames(state.manifest, targetTwilightProd, activeTwilightModel);
        if (!frames || frames.length === 0) {
          frames = ManifestTimelineHelper.getProductFrames(state.manifest, targetTwilightProd, 'gw_v2_blend_50');
        }
        const activeFrame = state.currentTimeStep
          ? (ManifestTimelineHelper.getClampedFrame(state.manifest, targetTwilightProd, activeTwilightModel, state.currentTimeStep)
            || ManifestTimelineHelper.getClampedFrame(state.manifest, targetTwilightProd, 'gw_v2_blend_50', state.currentTimeStep))
          : null;

        frames.forEach((frame) => {
          const fMs = frame.timestamp;
          if (isNaN(fMs) || fMs < startMs || fMs > endMs) return;

          const fPct = this.getTimePercentage(fMs, startMs, nowMs, endMs, totalSteps, nowIndex, range);
          if (fPct < leftPct) return;

          const hLower = (frame.hourStr || '').toLowerCase();
          const isMorning = hLower.includes('sunrise') || hLower.includes('morning');
          const isActiveFrame = !!(activeFrame && activeFrame.key === frame.key && activeFrame.timestamp === frame.timestamp);
          const fullDesc = this.formatForecastEventDescription(frame.hourStr, frame.dateStr) || `${frame.dateStr} ${frame.hourStr}`;

          const pillBtn = document.createElement('button');
          pillBtn.type = 'button';
          pillBtn.className = `celestial-timeline-pill ${isMorning ? 'morning' : 'evening'}${isActiveFrame ? ' active' : ''}`;
          pillBtn.style.left = `${fPct}%`;
          pillBtn.innerText = isMorning ? '🌅 AM' : '🌇 PM';
          pillBtn.title = `${fullDesc} — Click to jump to this event`;
          pillBtn.setAttribute('aria-label', `${fullDesc} — Jump to event`);
          pillBtn.setAttribute('aria-pressed', isActiveFrame ? 'true' : 'false');

          pillBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            store.update({ currentTimeStep: new Date(fMs) });
          });

          rowEl.appendChild(pillBtn);
        });
      }

      if ((prod === 'aurora' || prod === 'kp_index') && Array.isArray(state.noaaKpForecast) && state.noaaKpForecast.length > 0) {
        const curStepMs = state.currentTimeStep ? state.currentTimeStep.getTime() : 0;
        state.noaaKpForecast.forEach((item: any) => {
          if (!item.time_tag || item.kp === undefined) return;
          const timeStr = item.time_tag.endsWith('Z') ? item.time_tag : item.time_tag + 'Z';
          const kpMs = Date.parse(timeStr);
          if (isNaN(kpMs) || kpMs < startMs || kpMs > endMs) return;

          const kpPct = this.getTimePercentage(kpMs, startMs, nowMs, endMs, totalSteps, nowIndex, range);
          const isActiveKp = curStepMs > 0 && Math.abs(curStepMs - kpMs) < 90 * 60 * 1000;
          const kpPill = document.createElement('button');
          kpPill.type = 'button';
          kpPill.className = `kp-timeline-pill${isActiveKp ? ' active' : ''}`;
          kpPill.style.position = 'absolute';
          kpPill.style.left = `${kpPct}%`;
          kpPill.style.top = '50%';
          kpPill.style.transform = 'translate(-50%, -50%)';
          kpPill.style.fontSize = '0.62rem';
          kpPill.style.fontWeight = '800';
          kpPill.style.padding = '1px 5px';
          kpPill.style.borderRadius = '6px';
          kpPill.style.color = '#ffffff';
          kpPill.style.border = isActiveKp ? '1.5px solid #ffffff' : '1px solid rgba(255,255,255,0.25)';
          kpPill.style.zIndex = isActiveKp ? '5' : '3';
          kpPill.style.whiteSpace = 'nowrap';
          kpPill.style.boxShadow = isActiveKp ? '0 0 8px rgba(255,255,255,0.85)' : '0 1px 3px rgba(0,0,0,0.6)';
          kpPill.style.cursor = 'pointer';
          kpPill.style.pointerEvents = 'auto';

          const val = item.kp;
          let bgColor = '#10b981'; // Green (low Kp)
          if (val >= 7.0) bgColor = '#ec4899'; // Magenta (G3+ Severe Storm)
          else if (val >= 5.0) bgColor = '#f97316'; // Orange (G1/G2 Storm)
          else if (val >= 3.0) bgColor = '#f59e0b'; // Amber (Moderate)

          kpPill.style.backgroundColor = bgColor;
          kpPill.innerText = `Kp ${val.toFixed(1)}`;
          const obsLabel = item.observed || 'forecast';
          kpPill.title = `Kp ${val.toFixed(1)} (${obsLabel}) - ${new Date(kpMs).toUTCString()} (Click to scrub)`;
          kpPill.setAttribute('aria-label', `Kp ${val.toFixed(1)} at ${new Date(kpMs).toUTCString()}`);
          kpPill.setAttribute('aria-pressed', isActiveKp ? 'true' : 'false');

          kpPill.addEventListener('click', (e) => {
            e.stopPropagation();
            store.update({ currentTimeStep: new Date(kpMs) });
          });

          rowEl.appendChild(kpPill);
        });
      }

      this.availabilityContainer!.appendChild(rowEl);
    });
  }

  private renderScaleBar(state: any) {
    const scaleBar = document.getElementById('timeline-scale-bar');
    if (!scaleBar) return;

    const range = state.timelineRange;
    if (!range || range.length < 2) {
      scaleBar.innerHTML = '';
      this.lastScaleBarSig = 'empty';
      return;
    }

    const tz = state.userTimezone || this.defaultTimeZone;

    const totalSteps = range.length - 1;
    const nowIndex = ManifestTimelineHelper.getNowIndex(range);
    const firstKey = range[0]?.raw || `${range[0]?.date}_${range[0]?.hour}`;
    const nowKey = range[nowIndex]?.raw || `${range[nowIndex]?.date}_${range[nowIndex]?.hour}`;
    const endKey = range[totalSteps]?.raw || `${range[totalSteps]?.date}_${range[totalSteps]?.hour}`;
    const sig = `${tz}|${range.length}|${firstKey}|${nowKey}|${endKey}|${nowIndex}`;
    if (sig === this.lastScaleBarSig && scaleBar.childElementCount > 0) {
      return;
    }
    this.lastScaleBarSig = sig;
    scaleBar.innerHTML = '';

    const frag = document.createDocumentFragment();
    const nowStepMs = this.getStepDate(range[nowIndex]).getTime();
    const dFmt = this.getFormatter(`scale|${tz}`, {
      timeZone: tz,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    });
    const shortDateFmt = this.getFormatter(`shortDate|${tz}`, {
      timeZone: tz,
      month: 'short',
      day: 'numeric'
    });
    const processedMidnightDays = new Set<string>();
    const renderedPastHours = new Set<number>();
    let prevDayKey = '';
    
    range.forEach((step: any, idx: number) => {
      const pct = this.getStepPercentage(idx, nowIndex, totalSteps);

      if (idx < nowIndex) {
        // Dynamically compute past window ticks (-8h, -6h, -4h, -2h) based on actual minute difference from NOW
        const stepDate = this.getStepDate(step);
        const diffMins = Math.round((nowStepMs - stepDate.getTime()) / (60 * 1000));
        const exactHours = Math.round(diffMins / 60);

        // Only place ticks on exact 2-hour increments (8h, 6h, 4h, 2h) within 5 min of exact hour,
        // and deduplicate so each hour tick renders exactly once without repeating labels.
        if (
          diffMins > 0 &&
          Math.abs(diffMins - exactHours * 60) <= 5 &&
          exactHours % 2 === 0 &&
          exactHours <= 8 &&
          !renderedPastHours.has(exactHours)
        ) {
          renderedPastHours.add(exactHours);
          const tick = document.createElement('div');
          tick.className = 'scale-tick scale-subhour-tick';
          tick.style.left = `${pct}%`;
          if (pct < 1.5) {
            tick.style.transform = 'translateX(0)';
          }
          const labelStyle = `color: var(--color-liveweather, #00d4ff); font-weight: 800; ${pct < 1.5 ? 'left: 0; transform: none;' : ''}`;
          tick.innerHTML = `<span class="tick-sublabel" style="${labelStyle}">-${exactHours}h</span>`;
          frag.appendChild(tick);
        }
        return;
      }

      const stepDate = this.getStepDate(step);
      const parts = dFmt.formatToParts(stepDate);
      const yearStr = parts.find(p => p.type === 'year')?.value || '2026';
      const monthStr = parts.find(p => p.type === 'month')?.value || '1';
      const dayStr = parts.find(p => p.type === 'day')?.value || '1';
      const dayKey = `${yearStr}-${monthStr}-${dayStr}`;

      const rawHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
      const hour = rawHour === 24 ? 0 : rawHour;
      const min = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);

      let isMidnight = false;
      const isEnd = (idx === totalSteps);
      if (idx === nowIndex || isEnd) {
        isMidnight = true;
      } else if (prevDayKey && dayKey !== prevDayKey) {
        isMidnight = true;
      } else if (hour === 0 && !processedMidnightDays.has(dayKey)) {
        isMidnight = true;
      }
      prevDayKey = dayKey;

      if (isMidnight) {
        processedMidnightDays.add(dayKey);
        const formattedDate = shortDateFmt.format(stepDate);
        const timeStr = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

        const tick = document.createElement('div');
        tick.className = 'scale-tick scale-midnight-tick';
        tick.style.left = `${pct}%`;
        tick.innerHTML = `<span class="tick-label">${formattedDate}<br><span style="font-size: 8px; opacity: 0.65; display: block; margin-top: 2px;">${timeStr}</span></span>`;
        frag.appendChild(tick);
      } else {
        const isExtendedDay = (idx - nowIndex) > 120;
        const shouldShowSubhour = isExtendedDay ? (hour === 12 && min === 0) : (hour % 6 === 0 && min === 0);
        if (shouldShowSubhour) {
          // Collision avoidance: skip subhour tick if it's within 2 steps of nowIndex label
          if (idx - nowIndex >= 3) {
            const tick = document.createElement('div');
            tick.className = 'scale-tick scale-subhour-tick';
            tick.style.left = `${pct}%`;
            tick.innerHTML = `<span class="tick-sublabel">${String(hour).padStart(2, '0')}:00</span>`;
            frag.appendChild(tick);
          }
        }
      }
    });


    // Render NOW vertical line indicator at its linear position
    const nowPct = this.getStepPercentage(nowIndex, nowIndex, totalSteps);
    const nowMarker = document.createElement('div');
    nowMarker.className = 'timeline-now-marker';
    nowMarker.style.left = `${nowPct}%`;
    nowMarker.innerHTML = `<span class="timeline-now-label">NOW</span>`;
    frag.appendChild(nowMarker);
    scaleBar.appendChild(frag);
  }

  private renderSolarLunarTrack(state: any) {
    let trackEl = document.getElementById('timeline-solar-lunar-track');
    const oldBadges = document.getElementById('timeline-moon-badges');
    if (oldBadges) oldBadges.remove();

    if (state.timelineSunMoonPhaseEnabled === false) {
      if (trackEl) trackEl.style.display = 'none';
      this.lastSolarLunarSig = 'off';
      return;
    }

    const range = state.timelineRange;
    if (!range || range.length < 2) {
      if (trackEl) trackEl.innerHTML = '';
      this.lastSolarLunarSig = 'empty';
      return;
    }

    let lat = state.mapCenter[1];
    let lon = state.mapCenter[0];
    let locKey: string;
    if (state.selectedPoi) {
      lat = state.selectedPoi.lat;
      lon = state.selectedPoi.lng;
      locKey = `poi:${lat.toFixed(2)},${lon.toFixed(2)}`;
    } else {
      // Quantize map center to 0.25 deg so minor pans don't trigger 360+ SunCalc calls
      const qLat = (Math.round(lat * 4) / 4).toFixed(2);
      const qLon = (Math.round(lon * 4) / 4).toFixed(2);
      locKey = `map:${qLat},${qLon}`;
    }

    const totalSteps = range.length - 1;
    const nowIndex = ManifestTimelineHelper.getNowIndex(range);
    const firstKey = range[0]?.raw || `${range[0]?.date}_${range[0]?.hour}`;
    const nowKey = range[nowIndex]?.raw || `${range[nowIndex]?.date}_${range[nowIndex]?.hour}`;
    const endKey = range[totalSteps]?.raw || `${range[totalSteps]?.date}_${range[totalSteps]?.hour}`;
    const sig = `${range.length}|${firstKey}|${nowKey}|${endKey}|${nowIndex}|${locKey}`;

    if (trackEl && sig === this.lastSolarLunarSig && trackEl.childElementCount > 0) {
      if (trackEl.style.display !== 'flex') {
        trackEl.style.display = 'flex';
      }
      return;
    }
    this.lastSolarLunarSig = sig;

    if (!trackEl) {
      trackEl = document.createElement('div');
      trackEl.id = 'timeline-solar-lunar-track';
      trackEl.style.position = 'relative';
      trackEl.style.width = '100%';
      trackEl.style.height = '18px';
      trackEl.style.margin = '4px 0 4px 0';
      trackEl.style.display = 'flex';
      trackEl.style.alignItems = 'center';
      const insertTarget = this.slider.closest('.timeline-slider-wrapper') || this.slider;
      insertTarget.parentNode?.insertBefore(trackEl, insertTarget);
    }
    trackEl.style.display = 'flex';
    trackEl.style.background = 'transparent';
    trackEl.innerHTML = '';
    const nowPct = this.getStepPercentage(nowIndex, nowIndex, totalSteps);

    // 1. Draw "Sun & Moon Phase" label ending at the "NOW" marker
    const labelEl = document.createElement('div');
    labelEl.className = 'timeline-sun-moon-track-label';
    labelEl.style.position = 'absolute';
    labelEl.style.left = '0';
    labelEl.style.width = `${nowPct}%`;
    labelEl.style.height = '100%';
    labelEl.style.display = 'flex';
    labelEl.style.alignItems = 'center';
    labelEl.style.justifyContent = 'flex-end';
    labelEl.style.paddingRight = '8px';
    labelEl.style.fontSize = '11px';
    labelEl.style.fontWeight = '700';
    labelEl.style.color = '#e2e8f0';
    labelEl.style.whiteSpace = 'nowrap';
    labelEl.style.overflow = 'hidden';
    labelEl.style.textOverflow = 'ellipsis';
    labelEl.style.pointerEvents = 'none';
    labelEl.style.boxSizing = 'border-box';
    labelEl.innerText = 'Sun & Moon Phase';
    trackEl.appendChild(labelEl);

    // 2. Timeline gradient line only starts at the "NOW" marker and extends forward
    const futureSteps = range.slice(nowIndex);
    const futureTotal = futureSteps.length - 1;
    const colorStops: string[] = [];
    const overnightSteps: { idx: number; pct: number; sunAlt: number; date: Date }[] = [];

    futureSteps.forEach((step: any, futureIdx: number) => {
      const stepDate = this.getStepDate(step);
      const pct = futureTotal > 0 ? (futureIdx / futureTotal) * 100 : 0;

      const sunPos = SunCalc.getPosition(stepDate, lat, lon);
      const sunAltDeg = sunPos.altitude;

      const moonPos = SunCalc.getMoonPosition(stepDate, lat, lon);
      const moonAltDeg = moonPos.altitude;

      const moonIllum = SunCalc.getMoonIllumination(stepDate);

      let color = '#facc15';
      if (sunAltDeg > -0.83) {
        color = '#facc15'; // Day yellow
      } else if (sunAltDeg > -6.0) {
        color = '#f97316'; // Golden hour orange
      } else if (sunAltDeg > -12.0) {
        color = '#2563eb'; // Blue hour
      } else {
        // Night
        if (moonAltDeg <= 0) {
          color = '#030712'; // Black (Moon set)
        } else {
          const greyVal = Math.round(40 + moonIllum.fraction * 150);
          color = `rgb(${greyVal}, ${greyVal}, ${Math.min(255, greyVal + 20)})`;
        }
        const globalIdx = nowIndex + futureIdx;
        const globalPct = this.getStepPercentage(globalIdx, nowIndex, totalSteps);
        overnightSteps.push({ idx: globalIdx, pct: globalPct, sunAlt: sunAltDeg, date: stepDate });
      }

      colorStops.push(`${color} ${pct.toFixed(2)}%`);
    });

    const lineEl = document.createElement('div');
    lineEl.className = 'timeline-sun-moon-gradient-line';
    lineEl.style.position = 'absolute';
    lineEl.style.left = `${nowPct}%`;
    lineEl.style.width = `${Math.max(0, 100 - nowPct)}%`;
    lineEl.style.height = '4px';
    lineEl.style.borderRadius = '2px';
    lineEl.style.top = '50%';
    lineEl.style.transform = 'translateY(-50%)';
    lineEl.style.boxShadow = '0 1px 3px rgba(0,0,0,0.5)';
    if (colorStops.length > 0) {
      lineEl.style.background = `linear-gradient(to right, ${colorStops.join(', ')})`;
    }
    trackEl.appendChild(lineEl);

    if (overnightSteps.length > 0) {
      const nightGroups: Record<string, typeof overnightSteps> = {};
      overnightSteps.forEach(s => {
        const nightKey = s.date.toISOString().slice(0, 10);
        if (!nightGroups[nightKey]) nightGroups[nightKey] = [];
        nightGroups[nightKey].push(s);
      });

      Object.values(nightGroups).forEach(group => {
        let minStep = group[0];
        group.forEach(s => {
          if (s.sunAlt < minStep.sunAlt) minStep = s;
        });

        const moonPhase = getMoonPhaseDetails(minStep.date);
        const pctStr = moonPhase.illumination ? moonPhase.illumination.replace(/\s*Illumination/i, '').trim() : '';

        const badge = document.createElement('div');
        badge.className = 'timeline-moon-phase-badge';
        badge.style.position = 'absolute';
        badge.style.left = `${minStep.pct}%`;
        badge.style.top = '50%';
        badge.style.transform = 'translate(-50%, -50%)';
        badge.style.fontSize = '0.68rem';
        badge.style.fontWeight = '700';
        badge.style.color = '#f8fafc';
        badge.style.background = 'rgba(15, 23, 42, 0.9)';
        badge.style.border = '1px solid rgba(255, 255, 255, 0.25)';
        badge.style.borderRadius = '10px';
        badge.style.padding = '1px 6px';
        badge.style.display = 'flex';
        badge.style.alignItems = 'center';
        badge.style.gap = '4px';
        badge.style.zIndex = '3';
        badge.style.boxShadow = '0 2px 4px rgba(0,0,0,0.6)';
        badge.title = `${moonPhase.name} (${pctStr})`;
        badge.innerHTML = `<span>${moonPhase.icon}</span><span style="font-size: 0.65rem; opacity: 0.95;">${pctStr}</span>`;

        trackEl.appendChild(badge);
      });
    }
  }
}

export default Timeline;
